// @flow

import { Component } from '../jsti/core';
import EventNames from '../constants/EventNames';
import Hammer from 'hammerjs';
import Notifications from '../constants/Notifications';
import Time from '../constants/Time';
import type { Position } from '../interfaces/Position';
import DisableDraggingPlugin from '../plugins/DisableDraggingPlugin';
import { outerWidth } from '../classes/Helpers';

export default class ZoomImage extends Component {
  #doubleTapHandler: function;
  #hammerManager: Object;
  #zoomedRef: string;
  #container: HTMLElement;
  #parentElement: HTMLElement;
  #moveHandler: function;
  #imageHeight: number;
  #imageWidth: number;
  #zoomedImageHeight: number;
  #zoomedImageWidth: number;

  imageUrl: string;
  zoomedClass: string = 'zoomed';
  imagePosition: Position = { x: 0, y: 0 };
  mouseDownPosition: Position = { x: 0, y: 0 };
  mouseUpPosition: Position = { x: 0, y: 0 };
  speed: number = 1.5;
  scale: number = 4;

  maximumImageLoadTries: number = 20;
  usedImageLoadTries: number = 0;

  /**
   * Instantiate the component
   */
  bind(pEl: HTMLElement): void {
    super.bind(pEl);
    this.setUp();

    this.#doubleTapHandler = this.onDoubleTap.bind(this);
    this.#moveHandler = this.move.bind(this);

    this.#hammerManager = new Hammer.Manager(this.el);
    this.#hammerManager.add(new Hammer.Tap({ taps: 2, interval: Time.SECOND_IN_MILLISECONDS, posThreshold: 50 }));

    this.addEventListener('touchend', (event: Event) => event.preventDefault());

    this.#hammerManager.on(EventNames.DOUBLE_TAP, this.#doubleTapHandler);

    this.addListener(Notifications.RESET_ZOOM, this.resetZoom);

    new DisableDraggingPlugin([this.el]).activate();
  }

  /**
   * Setup properties and validate
   */
  setUp(): void {
    if (!(this.el instanceof HTMLImageElement)) {
      throw new Error('element must be a image');
    }

    if (!this.el.parentElement) {
      throw new Error('element must have a parent element');
    }

    this.#parentElement = document.getElementByRef('zoom-image-parent');

    this.waitForImageLoad(() => {
      if (!(this.el instanceof HTMLImageElement)) return;
      this.#imageHeight = this.el.height;
      this.#imageWidth = this.el.width;

      this.#zoomedImageHeight = this.#imageHeight * this.scale;
      this.#zoomedImageWidth = this.#imageWidth * this.scale;
    });

    this.#zoomedRef = this.el.dataset.zoomedRef;

    const container: ?HTMLElement = document.getElementByRef(this.#zoomedRef);
    if (!container) {
      throw new Error('Reference in parent container is required');
    }
    this.#container = container;
    this.imageUrl = this.el.src;
  }

  /**
   * Executes when the image is double tapped
   */
  onDoubleTap(): void {
    if (!this.#container.classList.contains(this.zoomedClass)) {
      this.zoom();
    } else {
      this.resetZoom();
    }
  }

  /**
   * `Zoom` the image
   */
  zoom(): void {
    if (!(this.el instanceof HTMLImageElement)) return;

    this.removeImgScrSets();

    this.el.src = this.imageUrl + '?width=' + this.#zoomedImageWidth + '&height=' + this.#zoomedImageHeight;

    this.waitForImageLoad(() => {
      this.#container.classList.add(this.zoomedClass);
      this.el.classList.add(this.zoomedClass);

      this.imagePosition.x = -Math.abs((this.#zoomedImageWidth / 2) - (outerWidth(this.#parentElement) / 2));
      this.imagePosition.y = -Math.abs((this.#zoomedImageHeight / 2) - (this.#parentElement.offsetHeight / 2));
      this.moveImage(this.imagePosition.x, this.imagePosition.y, this.#zoomedImageWidth, this.#zoomedImageHeight);

      document.addEventListener(EventNames.MOUSEDOWN, this.#moveHandler);
      document.addEventListener(EventNames.TOUCH_START, this.#moveHandler, { passive: true });
      this.notify(Notifications.ZOOMED);
    });
  }

  /**
   * deletes the zoomed class of the carousel modal
   */
  resetZoom(): void {
    if (!this.el.classList.contains(this.zoomedClass)) return;

    this.#container.classList.remove(this.zoomedClass);
    this.el.classList.remove(this.zoomedClass);
    this.moveImage(0, 0, this.#imageWidth, this.#imageHeight);
    this.imagePosition = { x: 0, y: 0 };
    document.removeEventListener(EventNames.MOUSEDOWN, this.#moveHandler);
    document.removeEventListener(EventNames.TOUCH_START, this.#moveHandler);
    this.notify(Notifications.RESETTED_ZOOM);
  }

  /**
   * moves the image
   */
  move(e: MouseEvent | TouchEvent): void {
    if (e.type === EventNames.MOUSEDOWN || e.type === EventNames.TOUCH_START) {
      document.addEventListener(EventNames.MOUSE_MOVE, this.#moveHandler);
      document.addEventListener(EventNames.MOUSEUP, this.#moveHandler);
      document.addEventListener(EventNames.TOUCH_MOVE, this.#moveHandler, { passive: true });
      document.addEventListener(EventNames.TOUCH_END, this.#moveHandler, { passive: true });

      this.mouseDownPosition.x = e instanceof MouseEvent ? e.clientX : e.changedTouches[0].clientX;
      this.mouseDownPosition.y = e instanceof MouseEvent ? e.clientY : e.changedTouches[0].clientY;
      return;
    }

    if (e.type === EventNames.MOUSE_MOVE || e.type === EventNames.TOUCH_MOVE) {
      const clientX = e instanceof MouseEvent ? e.clientX : e.changedTouches[0].clientX;
      const clientY = e instanceof MouseEvent ? e.clientY : e.changedTouches[0].clientY;

      const mouseDiffX = (clientX - this.mouseDownPosition.x) * this.speed;
      const mouseDiffY = (this.mouseDownPosition.y - clientY) * this.speed;

      let imageDiffX = this.imagePosition.x + mouseDiffX;
      let imageDiffY = this.imagePosition.y + mouseDiffY;

      imageDiffY = this.nextVerticalPosition(imageDiffY);
      imageDiffX = this.nextHorizontalPosition(imageDiffX);

      this.moveImage(imageDiffX, imageDiffY, this.#zoomedImageWidth, this.#zoomedImageHeight);
      return;
    }

    if (e.type === EventNames.MOUSEUP || e.type === EventNames.TOUCH_END) {
      document.removeEventListener(EventNames.MOUSE_MOVE, this.#moveHandler);
      document.removeEventListener(EventNames.MOUSEUP, this.#moveHandler);
      document.removeEventListener(EventNames.TOUCH_MOVE, this.#moveHandler);
      document.removeEventListener(EventNames.TOUCH_END, this.#moveHandler);

      this.mouseUpPosition.x = e instanceof MouseEvent ? e.clientX : e.changedTouches[0].clientX;
      this.mouseUpPosition.y = e instanceof MouseEvent ? e.clientY : e.changedTouches[0].clientY;

      const mouseDiffX = (this.mouseUpPosition.x - this.mouseDownPosition.x) * this.speed;
      const mouseDiffY = (this.mouseDownPosition.y - this.mouseUpPosition.y) * this.speed;

      const imageDiffX = this.imagePosition.x + mouseDiffX;
      const imageDiffY = this.imagePosition.y + mouseDiffY;

      this.imagePosition.x = this.nextHorizontalPosition(imageDiffX);
      this.imagePosition.y = this.nextVerticalPosition(imageDiffY);
    }
  }

  /**
   * Check if the horizontal position given is acceptable
   * If not. You have reached the boundary
   */
  nextHorizontalPosition(position: number): number {
    const imageWidth: number = this.el.offsetWidth;
    const parentWidth: number = outerWidth(this.#parentElement);

    if (position > 0) {
      return 0;
    } else if (imageWidth + position < parentWidth) {
      return -Math.abs(imageWidth - parentWidth);
    }

    return position;
  }

  /**
   * Check if the vertical position given is acceptable
   * If not. You have reached the boundary
   */
  nextVerticalPosition(position: number): number {
    const imageHeight: number = this.el.offsetHeight;
    const parentHeight: number = this.#parentElement.offsetHeight;

    if (position > 0) {
      return 0;
    } else if (imageHeight + position < parentHeight) {
      return -Math.abs(imageHeight - parentHeight);
    }

    return position;
  }

  /**
   * Move the image with the left and bottom properties and set the right height and width
   */
  moveImage(left: number, bottom: number, width: number, height: number): void {
    this.el.style.cssText = `left: ${left}px; bottom: ${bottom}px; width: ${width}px; height: ${height}px`;
  }

  /**
   * Check if the image is loaded
   * When it is fully loaded, execute the callback function
   */
  waitForImageLoad(callback: function): void {
    if (this.maximumImageLoadTries === this.usedImageLoadTries) return;

    if (!this.imageIsLoaded()) {
      this.usedImageLoadTries++;
      setTimeout(() => this.waitForImageLoad(callback), Time.HUNDRED_MILLISECONDS);
      return;
    }

    this.usedImageLoadTries = 0;
    callback();
  }

  /**
   * Check if the image is loaded
   */
  imageIsLoaded(): boolean {
    if (!(this.el instanceof HTMLImageElement)) return false;
    return this.el.complete && this.el.naturalHeight !== 0;
  }

  /**
   * Destroy all event listeners
   */
  destroy(): void {
    this.#hammerManager.destroy();
    super.destroy();
  }

  /**
   * Getter for the hammer manager property
   */
  get hammerManager(): Object {
    return this.#hammerManager;
  }

  /**
   * remove al source elements from the figure
   */
  removeImgScrSets() {
    if (this.el.parentElement instanceof HTMLPictureElement) {
      if (this.el.parentElement.firstElementChild instanceof HTMLSourceElement) {
        while (this.el.parentElement.firstElementChild instanceof HTMLSourceElement) {
          this.el.parentElement.firstElementChild.remove();
        }
      }
    }
  }
};
